import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
// import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import { fetch_one, fetch_ai } from "../../../lib/v31lib";
import { loginAtom } from '../../../lib/auth';

import Markdown from 'react-markdown';

import {
  Box,
  Button as Btn,
  Checkbox,
  CircularProgress,
  DialogActions,
  Grid,
} from '@mui/material';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import {
  Button,
  Typography
} from '../../../components/v2/styled';
import { Base } from './Base';
import { SnackCopied } from '../../../components/v2/snacks/Copied';

import { Advice } from '../../../models/Advice';
import { User } from '../../../models/User';

type Props = {
  // hidden?: boolean;
  setHidden?: boolean;
  title: string;
  open: boolean;
  setOpen: (b:boolean) => void;
  advice:  Advice;
  onClose: () => void;
};

export const AiCoachAdviceReport: FunctionComponent<Props> = ({title, advice, open, setOpen, onClose}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const theme = useTheme();
  // const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);

  const POLLING_INTERVAL = 1000;

  const [activeTask, setActiveTask] = useState<string>();
  const [taskLog, setTaskLog] = useState<string[]>([]);
  const [activeTaskStatus, setActiveTaskStatus] = useState<string>();
  const [fetchableResult, setFetchableResult] = useState<boolean>(false);
  const [taskResult, setTaskResult] = useState<string>();
  const [showMe, setShowMe] = useState<boolean>(false);
  const [copyOpen, setCopyOpen] = useState<boolean>(false);

  const {
    data: tempUser,
    isLoading: tempUserLoading,
    // isError: objectError,
    isSuccess: tempUserSuccess,
    // refetch: objectRefetch,
  } = useQuery({
    queryKey: ["user", 'full', advice.user_id],
    queryFn: () =>
      fetch_one<User>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'user',
          id: advice.user_id,
          fields: [
            'id', 'full_name', 'ai_data', 'custom_api_get_intake_progress'
          ],
          practice_id: login?.practice_id
        },
        login
      ),
    enabled: parseInt(advice.user_id?.toString() || '', 10) > 0,
  });






  const mutationCustomAction = useMutation({
    mutationFn: (pars: {
      url: string;
      formData: any;
      mode: string;
    }) => {
      return fetch_ai<any>(
        pars.url,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      return true;
    },
    onError: (data, variables, context) => {
      console.log('err', data);
      return true;
    },
    onSuccess: (data, variables, context) => {
      console.log('succ', data);

      if (variables.mode === 'init') {
        if (data.queueId) {
          setActiveTask(data.queueId);
          setTaskLog([...taskLog, 'QueueId received...']);
        }
      }
      if (variables.mode === 'queue') {
        setActiveTaskStatus(data.status);
        if (data.status === 'processing') {
          setTaskLog([...taskLog, `Status: ${data.status}. Step: ${data.currentState}`]);
        }
        if (data.status === 'completed') {
          setFetchableResult(data.resultAvailable);
          setTaskLog([...taskLog, `Completed at ${data.completedAt}!`]);
          setTaskLog([...taskLog, `Result is ${!!data.resultAvailable ? '' : 'un'}available!`]);
        }
        if (data.status === 'error') {
          setTaskLog([...taskLog, `Error occurred!!`]);
        }
      }
      if (variables.mode === 'result') {
        setTaskLog([...taskLog, 'Result fetched...']);
        // data.anonymizedMappings
        setTaskResult(data.result);
      }
    },
  });








  // @ts-ignore
  const useInterval = (callback, delay) => {
    const savedCallback = useRef();
   
    // Remember the latest callback.
    useEffect(() => {
      savedCallback.current = callback;
    }, [callback]);
   
    // Set up the interval.
    useEffect(() => {
      function tick() {
        // @ts-ignore
        savedCallback.current();
      }
      if (delay !== null) {
        let id = setInterval(tick, delay);
        return () => clearInterval(id);
      }
    }, [delay]);
  }

  const poll = () => {
    if (!!activeTask && activeTaskStatus !== 'completed' && activeTaskStatus !== 'error') {
      // setTaskLog([...taskLog, 'Fetching queue status... ']);
      mutationCustomAction.mutate({
        url: `/api/status`,
        formData: {
          queueId: activeTask
        },
        mode: 'queue'
      });
    }
  }

  useEffect(() => {
    if (!!activeTask && activeTaskStatus === 'completed' && fetchableResult) {
      mutationCustomAction.mutate({
        url: `/api/result`,
        formData: {
          queueId: activeTask
        },
        mode: 'result'
      });
    }
  }, [activeTask, activeTaskStatus, fetchableResult]);

  useInterval(() => {
    poll();
  }, POLLING_INTERVAL);

  return (<>
    <Base
      name="advice_report"
      hidden={!open}
      title={title}
      content={<Grid container spacing={2}>
        {!taskResult && <>
          <Grid item xs={12}>
            <Typography>Genereer een rapport op basis van deze consultatie, met als bijkomende info de vorige consultaties, het intakeprofiel, en enkele velden uit het gebruikersprofiel.</Typography>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{
              border: '1px solid transparent',
              borderColor: theme.palette.primary.main,
              borderRadius: 1
            }}>
              <Box sx={{
                borderBottom: '1px solid transparent',
                borderColor: theme.palette.primary.main,
                width: '100%',
                padding: 1,
              }}>
                <Typography>Consultatie van {advice.advice_date}.</Typography>
              </Box>
              {!!tempUserLoading && <Box sx={{
                width: '100%',
                padding: 1,
              }}>
                <CircularProgress />
              </Box>}
              {!!tempUserSuccess && <>
                <Box sx={{
                  borderBottom: '1px solid transparent',
                  borderColor: theme.palette.primary.main,
                  width: '100%',
                  padding: 1,
                }}>
                  <Typography sx={{fontWeight: 'bold'}}>{tempUser.full_name}</Typography>
                </Box>
                <Box sx={{
                  borderBottom: '1px solid transparent',
                  borderColor: theme.palette.primary.main,
                  width: '100%',
                  padding: 1,
                }}>
                  <Typography>Profielvelden: {!!tempUser.ai_data?.profile.birth_date && !!tempUser.ai_data?.profile.gender ? '100%' : (!!tempUser.ai_data?.profile.birth_date || !!tempUser.ai_data?.profile.gender ? '50%' : '0%')}</Typography>
                </Box>
                <Box sx={{
                  borderBottom: '1px solid transparent',
                  borderColor: theme.palette.primary.main,
                  width: '100%',
                  padding: 1,
                }}>
                  <Typography>Intakeprofiel: {tempUser.custom_api_get_intake_progress}%</Typography>
                </Box>
                <Box sx={{
                  width: '100%',
                  padding: 1,
                }}>
                  <Typography>Aantal eerdere consultaties: {(tempUser.ai_data?.advices || []).filter(fff => (fff.id || 0) < (advice.id || 0)).length || 0}</Typography>
                </Box>
              </>}
            </Box>
          </Grid>
          {!!tempUser && <Grid item xs={12}>
            <Button
              onClick={(e) => {
                mutationCustomAction.mutate({
                  url: '/api/process',
                  formData: {
                    data: {
                      custom_result: {
                        ...tempUser.ai_data,
                        advices: [
                          ...(tempUser.ai_data?.advices || []).filter(fff => (fff.id || 0) <= (advice.id || 0))
                        ]
                      }
                    }
                  },
                  mode: 'init'
                });
              }}
              startIcon={<AutoAwesomeIcon />}
              loading={mutationCustomAction.isPending}
              contained
              id="start_report"
              sx={{
                marginTop: 1
              }}
              label={t("coach.general.actions.start_report", 'Start met het genereren')}
            />
          </Grid>}
          {!!activeTask && <>
            <Grid item xs={12}>
              <Box sx={{
                backgroundColor: 'white',
                padding: 2,
                paddingLeft: 4,
                border: '1px solid transparent',
                borderLeft: '5px solid transparent',
                borderColor: theme.palette.warning.main,
                borderRadius: 1
              }}>
                {taskLog.slice(-10).map(l => <Typography sx={{fontFamily: 'Roboto Mono, monospace', fontSize: '12px'}}>{l}</Typography>)}
              </Box>
            </Grid>
          </>}
        </>}
        {!!taskResult && <>
          <Grid item xs={12}>
            <Typography sx={{
              color: 'red'
            }}>Dit rapport wordt niet opgeslagen. Alle data werd ge-anonimiseerd vooraleer de NutriAI deze verwerkte. <b>Lees aandachtig na!</b>. Dit gegenereerd rapport is bedoeld als startpunt voor uw ICF-conform rapport. Copy-paste in Word of een andere tekst-editor om verder af te werken.</Typography>
          </Grid>
          <Grid item xs={12} sx={{
            display: "flex",
            alignItems: "center",
            "& > label": {
              padding: 1
            },
            "&.error": {
              border: "0.5px solid rgba(255, 0, 0, 0.5)"
            }
          }}>
            <Checkbox
              checked={showMe}
              onChange={(e) => {
                setShowMe(e.target.checked);
              }}
              value="gdpr"
            />
            <Typography html onClick={(e) => {
              setShowMe(!showMe);
            }}>Ik begrijp het. Toon mij het rapport.</Typography>
          </Grid>
          {!!showMe && <Grid item xs={12}>
            <Button
              onClick={(e) => {
                e.preventDefault();
                navigator.clipboard.writeText(taskResult);
                setCopyOpen(true);
              }}
              startIcon={<ContentCopyIcon />}
              contained
              id="copy_report"
              sx={{
                marginTop: 1
              }}
              label={t("coach.general.actions.copy")}
            />
          </Grid>}
          {!!showMe && <Grid item xs={12}>
            <Box sx={{
              backgroundColor: 'white',
              padding: 2,
              paddingLeft: 4,
              border: '1px solid transparent',
              borderLeft: '5px solid transparent',
              borderColor: theme.palette.warning.main,
              borderRadius: 1,
              "h2": {
                marginTop: 1,
                marginBottom: 0,
                color: theme.palette.primary.main
              },
              "ul": {
                marginTop: 0
              }
            }}>
              <Markdown>{taskResult}</Markdown>
            </Box>
          </Grid>}
          {!!showMe && <Grid item xs={12}>
            <Button
              onClick={(e) => {
                e.preventDefault();
                navigator.clipboard.writeText(taskResult);
                setCopyOpen(true);
              }}
              startIcon={<ContentCopyIcon />}
              contained
              id="copy_report"
              sx={{
                marginTop: 1
              }}
              label={t("coach.general.actions.copy")}
            />
          </Grid>}
        </>}
      </Grid>}
      actions={<DialogActions>
        <Btn autoFocus onClick={() => {
          setOpen(false);
          onClose();
        }}>{t("client.general.actions.cancel")}</Btn>
      </DialogActions>}
      open={open}
      setOpen={setOpen}
    />
    <SnackCopied open={copyOpen} setOpen={setCopyOpen} />
  </>);
}